import React, {useState, useRef, useEffect} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {aiBatchReviewAction, transcribeAudioAction} from "../../../actions/clientAction";
import {chatMessageAddAction} from "../../../actions/chatAction";
import {toast} from "react-toastify";
import {formatErrors} from "../../../constants";


const ChatAiBatchReview = ({aiBatch, clientId, onComplete}) => {
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [commentError, setCommentError] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const dispatch = useDispatch();
  const chatID = useSelector(state => state.chat.chatID);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {type: 'audio/webm'});
        stream.getTracks().forEach(t => t.stop());
        setRecording(false);
        setTranscribing(true);

        dispatch(transcribeAudioAction(blob)).then((text) => {
          setComment(prev => prev ? prev + ' ' + text : text);
          setTranscribing(false);
        }).catch(() => {
          setTranscribing(false);
          toast.error('Не удалось распознать аудио', {position: toast.POSITION.BOTTOM_RIGHT});
        });
      };

      mediaRecorder.start();
      setRecording(true);
      setCommentError(false);
    } catch (err) {
      toast.error('Не удалось получить доступ к микрофону', {position: toast.POSITION.BOTTOM_RIGHT});
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  const handleApprove = () => {
    if (loading) return;
    setLoading(true);
    return dispatch(aiBatchReviewAction({
      client_id: clientId,
      batch_end: aiBatch.batch_end,
      status: 'approved'
    })).then(() => {
      return dispatch(chatMessageAddAction(clientId, chatID, 1, aiBatch.generated_response, null, null, null));
    }).then(() => {
      toast.success('AI ответ применён и отправлен в чат', {position: toast.POSITION.BOTTOM_RIGHT});
      onComplete();
      return true;
    }).catch((err) => {
      toast.error(formatErrors([err.message]), {position: toast.POSITION.BOTTOM_RIGHT});
      return false;
    }).finally(() => {
      setLoading(false);
    });
  };

  const handleDecline = () => {
    if (loading) return;

    if (!comment.trim()) {
      setCommentError(true);
      return;
    }

    setLoading(true);

    dispatch(aiBatchReviewAction({
      client_id: clientId,
      batch_end: aiBatch.batch_end,
      status: 'declined',
      comment: comment.trim()
    })).then(() => {
      toast.success('AI ответ отклонён', {position: toast.POSITION.BOTTOM_RIGHT});
      onComplete();
    }).catch((err) => {
      toast.error(formatErrors([err.message]), {position: toast.POSITION.BOTTOM_RIGHT});
    }).finally(() => {
      setLoading(false);
    });
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const pad = (n) => String(n).padStart(2, '0');
    return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear() + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
  };

  return <div className="user-info__ai-batch">
    <div className="user-info__wrapper">
      <p className="help">Сообщения клиента</p>
      {aiBatch.messages && aiBatch.messages.map((msg, idx) => (
        <div key={idx} className="user-info__ai-message">
          <span className="user-info__ai-time">{formatTime(msg.created)}</span>
          <p className="user-info__text">{msg.message}</p>
          {msg.file && <p className="user-info__text user-info__ai-file">{msg.file}</p>}
        </div>
      ))}
    </div>
    <div className="user-info__wrapper">
      <p className="help">AI ответ</p>
      <div className="user-info__ai-response">{aiBatch.generated_response}</div>
    </div>
    <div className="user-info__wrapper">
      <div className="user-info__ai-comment-row">
        <textarea
          className={"input" + (commentError ? " input--error" : "")}
          placeholder="Комментарий (обязателен для отклонения)"
          value={comment}
          onChange={(e) => { setComment(e.target.value); setCommentError(false); }}
          rows={5}
        />
        {recording ? (
          <button className="user-info__ai-mic user-info__ai-mic--active" onClick={stopRecording} title="Остановить запись">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
          </button>
        ) : (
          <button className="user-info__ai-mic" onClick={startRecording} disabled={transcribing} title="Записать голосовой комментарий">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#666"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/></svg>
          </button>
        )}
      </div>
      {(recording || transcribing) && (
        <div className="user-info__ai-recording">
          <span className="user-info__ai-recording-dot"></span>
          <span>{transcribing ? 'Распознаётся...' : 'Запись...'}</span>
        </div>
      )}
    </div>
    <div className="user-info__ai-actions">
      <button className="btn btn--accent" onClick={handleApprove} disabled={loading || transcribing}>Применить</button>
      <button className="btn" onClick={handleDecline} disabled={loading || transcribing}>Отклонить</button>
    </div>
  </div>;
};

export {ChatAiBatchReview};
